import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, BookOpen, RotateCcw, ScanLine, Undo2 } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import {
  circulationApi,
  libraryDashboardApi,
  libraryMembersApi,
} from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { formatDate } from '@/utils/format';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import type { LibraryMember, ReturnResult } from '@/types/library';
import { Blockers, LoanStatusBadge, MemberStatusBadge, Money, Stat, overdueText } from './shared';

type Mode = 'issue' | 'return';

/**
 * The issue desk.
 *
 * <p>Built around the scanner, because that is what the work is: §16 asks for
 * scan member → scan book → confirm, and every field here is a text input a
 * barcode reader can type into. The scanner acts as a keyboard that ends with
 * Enter, so each input submits on Enter and then re-focuses itself for the next
 * scan — a librarian working through a queue never touches the mouse.
 *
 * <p>Nothing on this screen decides anything. Whether a member may borrow, what
 * a book is due back, whether a fine is owed and how much: all of it comes from
 * the server, and the refusals are shown in the server's own words. §38 is
 * explicit that a critical rule must not live only in React, and the corollary
 * is that the explanation must not either.
 */
export function CirculationDeskPage() {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<Mode>('issue');
  const [member, setMember] = useState<LibraryMember | null>(null);
  const [memberCode, setMemberCode] = useState('');
  const [copyCode, setCopyCode] = useState('');
  const [lastReturn, setLastReturn] = useState<ReturnResult | null>(null);

  const memberInput = useRef<HTMLInputElement>(null);
  const copyInput = useRef<HTMLInputElement>(null);

  const desk = useQuery({
    queryKey: queryKeys.library.circulationDashboard,
    queryFn: () => libraryDashboardApi.circulation(),
  });

  /**
   * The member's eligibility, refetched whenever they change.
   *
   * Shown before a book is scanned so the librarian finds out about a
   * suspension or a blocking fine while the reader is still walking up, rather
   * than after the barcode beep.
   */
  const eligibility = useQuery({
    queryKey: queryKeys.libraryMembers.eligibility(member?.id ?? ''),
    queryFn: () => libraryMembersApi.eligibility(member!.id),
    enabled: member !== null,
  });

  const loans = useQuery({
    queryKey: queryKeys.circulation.memberLoans(member?.id ?? ''),
    queryFn: () => circulationApi.memberLoans(member!.id),
    enabled: member !== null,
  });

  // Focus follows the workflow: member first, then the book.
  useEffect(() => {
    if (mode === 'return') {
      copyInput.current?.focus();
    } else if (member === null) {
      memberInput.current?.focus();
    } else {
      copyInput.current?.focus();
    }
  }, [mode, member]);

  const findMember = useMutation({
    mutationFn: (code: string) => libraryMembersApi.scan(code),
    onSuccess: (found) => {
      setMember(found);
      setMemberCode('');
    },
    onError: (error) => handleApiError(error),
  });

  const issue = useMutation({
    mutationFn: (code: string) =>
      circulationApi.issue({ memberId: member!.id, copyCode: code }),
    onSuccess: (loan) => {
      toast.success(`${loan.bookTitle} — due back ${formatDate(loan.dueDate)}`);
      setCopyCode('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.circulation.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryMembers.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
      copyInput.current?.focus();
    },
    onError: (error) => handleApiError(error),
  });

  const returnCopy = useMutation({
    mutationFn: (code: string) => circulationApi.returnCopy({ copyCode: code }),
    onSuccess: (result) => {
      setLastReturn(result);
      setCopyCode('');

      if (result.heldForMemberName !== undefined) {
        // The most important thing a return can tell a librarian: this one does
        // not go back on the shelf.
        toast.success(`Hold it for ${result.heldForMemberName}`);
      } else if (result.fineAmount !== undefined && result.fineAmount > 0) {
        toast.error(`${overdueText(result.daysOverdue)} — a fine has been raised`);
      } else {
        toast.success('Returned');
      }

      void queryClient.invalidateQueries({ queryKey: queryKeys.circulation.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryFines.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
      copyInput.current?.focus();
    },
    onError: (error) => handleApiError(error),
  });

  const renew = useMutation({
    mutationFn: (loanId: string) => circulationApi.renew(loanId),
    onSuccess: (loan) => {
      toast.success(`Renewed — now due ${formatDate(loan.dueDate)}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.circulation.all });
    },
    onError: (error) => handleApiError(error),
  });

  function clearMember() {
    setMember(null);
    setMemberCode('');
    setCopyCode('');
    memberInput.current?.focus();
  }

  const blockers = eligibility.data?.blockers ?? [];
  const canBorrow = eligibility.data?.canBorrow ?? false;

  return (
    <div>
      <PageHeader
        title="Issue desk"
        description="Scan the card, scan the book, confirm."
        icon={<ArrowLeftRight className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="mb-3 flex gap-2">
        <ModeButton active={mode === 'issue'} onClick={() => setMode('issue')} icon={BookOpen}>
          Issue
        </ModeButton>
        <ModeButton active={mode === 'return'} onClick={() => setMode('return')} icon={Undo2}>
          Return
        </ModeButton>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          {mode === 'issue' ? (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Who is borrowing
              </h2>

              {member === null ? (
                <form
                  className="mt-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (memberCode.trim() !== '') {
                      findMember.mutate(memberCode.trim());
                    }
                  }}
                >
                  <ScanField
                    ref={memberInput}
                    label="Membership number or student number"
                    value={memberCode}
                    onChange={setMemberCode}
                    placeholder="Scan a card…"
                    busy={findMember.isPending}
                  />
                </form>
              ) : (
                <div className="mt-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-lg font-extrabold text-ink">{member.name}</p>
                      <p className="text-sm text-muted">
                        {member.membershipNumber} · {member.memberType.toLowerCase()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <MemberStatusBadge status={member.status} />
                      <Button variant="ghost" size="sm" onClick={clearMember}>
                        <RotateCcw className="h-4 w-4" aria-hidden="true" />
                        Different reader
                      </Button>
                    </div>
                  </div>

                  <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <MiniStat label="Out" value={`${member.booksOut} of ${member.borrowingLimit}`} />
                    <MiniStat label="Loan period" value={`${member.loanDays} days`} />
                    <MiniStat label="Renewals" value={String(member.maxRenewals)} />
                    <MiniStat
                      label="Fines"
                      value={<Money value={member.outstandingFines} />}
                    />
                  </dl>

                  {eligibility.isPending ? (
                    <div className="mt-3 flex justify-center py-4">
                      <Spinner />
                    </div>
                  ) : (
                    <div className="mt-3">
                      <Blockers reasons={blockers} title="Cannot borrow" />
                    </div>
                  )}

                  {canBorrow && (
                    <form
                      className="mt-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (copyCode.trim() !== '') {
                          issue.mutate(copyCode.trim());
                        }
                      }}
                    >
                      <ScanField
                        ref={copyInput}
                        label="Book barcode or accession number"
                        value={copyCode}
                        onChange={setCopyCode}
                        placeholder="Scan a book…"
                        busy={issue.isPending}
                      />
                    </form>
                  )}
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Taking a book back
              </h2>
              <p className="mt-0.5 text-xs text-muted">
                The book's own barcode is enough — a returned book carries nothing else.
              </p>

              <form
                className="mt-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (copyCode.trim() !== '') {
                    returnCopy.mutate(copyCode.trim());
                  }
                }}
              >
                <ScanField
                  ref={copyInput}
                  label="Book barcode or accession number"
                  value={copyCode}
                  onChange={setCopyCode}
                  placeholder="Scan a book…"
                  busy={returnCopy.isPending}
                />
              </form>

              {lastReturn !== null && <ReturnOutcome result={lastReturn} />}
            </Card>
          )}

          {member !== null && mode === 'issue' && (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                What they have out
              </h2>
              {loans.isPending ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : loans.data === undefined || loans.data.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">Nothing out.</p>
              ) : (
                <ul className="mt-2 divide-y divide-line">
                  {loans.data.map((loan) => (
                    <li key={loan.id} className="flex flex-wrap items-center gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink">{loan.bookTitle}</p>
                        <p className="text-xs text-muted">
                          {loan.accessionNumber} · due {formatDate(loan.dueDate)}
                          {loan.overdue && ` · ${overdueText(loan.daysOverdue)}`}
                        </p>
                      </div>
                      <LoanStatusBadge status={loan.status} overdue={loan.overdue} />
                      {loan.renewable ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => renew.mutate(loan.id)}
                          disabled={renew.isPending}
                        >
                          Renew
                        </Button>
                      ) : (
                        // Why not, in the server's words rather than ours.
                        <span
                          className="text-xs text-muted"
                          title={loan.renewalBlockers.join('; ')}
                        >
                          {loan.renewalBlockers[0] ?? 'Cannot renew'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>

        <DeskSummary />
      </div>
    </div>
  );

  function DeskSummary() {
    if (desk.isError) {
      return <ErrorState error={desk.error} onRetry={() => void desk.refetch()} />;
    }
    if (desk.isPending) {
      return (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      );
    }

    const data = desk.data;

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Issued today" value={data.issuedToday} />
          <Stat label="Returned today" value={data.returnedToday} />
          <Stat label="Renewed today" value={data.renewedToday} />
          <Stat
            label="Overdue"
            value={data.overdueLoans}
            tone={data.overdueLoans > 0 ? 'danger' : 'neutral'}
          />
        </div>

        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Waiting to be collected
          </h2>
          <p className="mt-0.5 text-xs text-muted">The shelf behind the desk.</p>
          {data.awaitingCollection.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted">Nothing waiting.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {data.awaitingCollection.map((hold) => (
                <li key={hold.reservationId} className="py-2">
                  <p className="truncate text-sm font-semibold text-ink">{hold.title}</p>
                  <p className="text-xs text-muted">
                    {hold.memberName} · {hold.accessionNumber ?? '—'}
                  </p>
                  <p
                    className={`text-xs ${hold.lapsed ? 'text-[color:var(--sms-danger)]' : 'text-muted'}`}
                  >
                    {hold.lapsed ? 'Not collected — ' : 'Collect by '}
                    {hold.collectBy === undefined ? '—' : formatDate(hold.collectBy)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Worst overdue
            </h2>
            <Link
              to={ROUTES.overdueLoans}
              className="text-xs text-accent underline-offset-2 hover:underline"
            >
              All of them
            </Link>
          </div>
          {data.mostOverdue.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted">Nothing is late.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {data.mostOverdue.slice(0, 8).map((row) => (
                <li key={row.transactionId} className="py-2">
                  <p className="truncate text-sm font-semibold text-ink">{row.title}</p>
                  <p className="text-xs text-muted">
                    {row.memberName} · {overdueText(row.daysOverdue)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    );
  }
}

/** What a return produced: the fine, and who the copy is now held for. */
function ReturnOutcome({ result }: { result: ReturnResult }) {
  const held = result.heldForMemberName !== undefined;
  const fined = result.fineAmount !== undefined && result.fineAmount > 0;

  return (
    <div
      className={`mt-3 border p-3 ${
        held
          ? 'border-[color:var(--sms-warning)]'
          : fined
            ? 'border-[color:var(--sms-danger)]'
            : 'border-line'
      }`}
    >
      <p className="text-sm font-extrabold text-ink">{result.loan.bookTitle}</p>
      <p className="text-xs text-muted">
        {result.loan.accessionNumber} · {result.loan.memberName}
      </p>

      {held ? (
        <p className="mt-2 text-sm font-semibold text-[color:var(--sms-warning)]">
          Do not re-shelve — held for {result.heldForMemberName}
        </p>
      ) : (
        <p className="mt-2 text-sm text-muted">Back on the shelf.</p>
      )}

      {result.daysOverdue > 0 && (
        <p className="mt-1 text-sm text-ink">
          {overdueText(result.daysOverdue)}
          {fined ? (
            <>
              {' — '}
              <Money value={result.fineAmount} bold />
            </>
          ) : (
            // A late book with no fine has a reason, and the librarian will be
            // asked what it was.
            result.noFineReason !== undefined && ` — ${result.noFineReason}`
          )}
        </p>
      )}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.04em] text-muted">{label}</dt>
      <dd className="text-sm font-semibold text-ink">{value}</dd>
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-2 border px-3 py-2 text-sm font-semibold ${
        active ? 'border-accent bg-accent-soft text-accent-soft-fg' : 'border-line bg-bg text-muted'
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </button>
  );
}

/**
 * A field a barcode reader types into.
 *
 * The reader behaves as a keyboard ending in Enter, so the enclosing form
 * submits on its own and the input keeps focus for the next scan.
 */
const ScanField = ((props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  busy: boolean;
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const { label, value, onChange, placeholder, busy, ref } = props;
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">{label}</span>
      <span className="mt-1 flex items-center gap-2 border border-line bg-bg px-3 py-2 focus-within:border-accent">
        <ScanLine className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        <input
          ref={ref}
          className="w-full bg-transparent text-base text-ink outline-none"
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
          onChange={(event) => onChange(event.target.value)}
        />
        {busy && <Spinner />}
      </span>
    </label>
  );
}) as (props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  busy: boolean;
  ref?: React.Ref<HTMLInputElement>;
}) => JSX.Element;
